export function unwrapPackage(res) {
  if (!res || typeof res !== 'object') return null
  if (
    res.data &&
    typeof res.data === 'object' &&
    !Array.isArray(res.data) &&
    (res.data.id != null || res.data.price != null || res.data.name)
  ) {
    return res.data
  }
  return res
}

export function packagePrice(pkg) {
  const n = Number(pkg?.price)
  return Number.isFinite(n) ? n : 0
}

/** Un paquete es suero solo por el flag del backend; renombrar el treatment IV no debe afectarlo. */
export function isIvTherapyPackage(pkg) {
  return !!pkg && typeof pkg === 'object' && pkg.is_iv_serum === true
}

export function findIvTherapiesTreatment(treatments) {
  if (!Array.isArray(treatments)) return null
  return treatments.find(item => item?.is_iv_serum_marker === true) || null
}

export function packageKindFromTreatments(treatmentIds, ivTreatment) {
  if (!ivTreatment || ivTreatment.id == null) return 'normal'
  const ids = Array.isArray(treatmentIds) ? treatmentIds : []
  return ids.some(id => Number(id) === Number(ivTreatment.id)) ? 'iv' : 'normal'
}

export function syncTreatmentsForPackageKind(kind, treatmentIds, ivTreatment) {
  const ids = Array.isArray(treatmentIds) ? treatmentIds.filter(id => id != null) : []
  if (!ivTreatment || ivTreatment.id == null) return { ids, ivId: null, missing: true }
  const ivId = ivTreatment.id
  const has = ids.some(id => Number(id) === Number(ivId))
  if (kind === 'iv') {
    return { ids: has ? ids : [...ids, ivId], ivId, missing: false }
  }
  return { ids: ids.filter(id => Number(id) !== Number(ivId)), ivId, missing: false }
}

/** Entero ≥ 1 o null: la cantidad inválida se reporta, nunca se corrige en silencio. */
export function parseIntegerQty(value) {
  if (value === '' || value == null) return null
  const n = Number(value)
  return Number.isInteger(n) && n >= 1 ? n : null
}

/** Precio mostrado de una línea de suero: lo cobrado o, en visitas anteriores al cambio, el del paquete. */
export function ivSerumPrice(line) {
  const charged = line?.price_charged
  if (charged != null && charged !== '') return Number(charged) || 0
  return packagePrice(line?.package)
}

/** Líneas de suero de una visita (`iv_serums` o `iv_visit_serums`, según el endpoint). */
export function ivSerumLines(source) {
  if (!source || typeof source !== 'object') return []
  const lines = source.iv_serums || source.iv_visit_serums
  return Array.isArray(lines) ? lines : []
}

/**
 * Filas de recibo/detalle: cada suero seguido de sus vitaminas.
 * `addons[].price_charged` ya es el total de la línea: no se multiplica por `qty`.
 */
export function buildIvInvoiceLines(serumLines) {
  const rows = []
  ;(serumLines || []).forEach((line, index) => {
    rows.push({
      key: `serum-${line.iv_visit_serum_id || index}`,
      name: line.package?.name || 'IV serum',
      qty: 1,
      price: ivSerumPrice(line).toFixed(2),
      isAddon: false,
    })
    ;(line.addons || []).forEach(addon => {
      rows.push({
        key: `addon-${addon.iv_visit_addon_id || `${index}-${addon.iv_addon_id}`}`,
        name: addon.name || 'Extra vitamin',
        qty: parseInt(addon.qty, 10) || 1,
        price: Number(addon.price_charged || 0).toFixed(2),
        isAddon: true,
      })
    })
  })
  return rows
}

/** El paquete se muestra si no es suero o si la visita no trae líneas de suero. */
export function shouldShowPackage(pkg, serumLines) {
  if (!pkg) return false
  return pkg.is_iv_serum !== true || !(serumLines && serumLines.length)
}

function isBlankFacility(value) {
  return value == null || value === ''
}

function sameFacility(a, b) {
  if (isBlankFacility(a) || isBlankFacility(b)) return false
  return Number(a) === Number(b)
}

function rowFacilityId(row) {
  if (!row || typeof row !== 'object') return null
  if (!isBlankFacility(row.facility_id)) return Number(row.facility_id)
  if (row.facility && !isBlankFacility(row.facility.id)) return Number(row.facility.id)
  return null
}

/** Parse iv-prices `effective_date` (`m-d-Y` / `m-d-Y H:i:s` / ISO) to ms. */
export function parseEffectiveTimestamp(value) {
  if (value == null || value === '') return null
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.getTime()

  const str = String(value).trim()
  const mdy = str.match(
    /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/
  )
  if (mdy) {
    const date = new Date(
      Number(mdy[3]),
      Number(mdy[1]) - 1,
      Number(mdy[2]),
      Number(mdy[4] || 0),
      Number(mdy[5] || 0),
      Number(mdy[6] || 0)
    )
    return Number.isNaN(date.getTime()) ? null : date.getTime()
  }

  const parsed = Date.parse(str)
  return Number.isNaN(parsed) ? null : parsed
}

/**
 * Add-ons sellable in the OMA for a facility: latest iv-prices row
 * effective on or before the visit date. Facility price beats global on a tie.
 */
export function resolveIvAddonsForFacility(prices, addons, facilityId, asOfDate) {
  const asOf = parseEffectiveTimestamp(asOfDate) || Date.now()
  const addonMap = {}
  ;(addons || []).forEach(addon => {
    if (addon && addon.id != null) addonMap[Number(addon.id)] = addon
  })

  const winners = {}
  ;(prices || []).forEach(row => {
    const priceFacility = rowFacilityId(row)
    const isGlobal = priceFacility == null
    const isThisFacility = sameFacility(priceFacility, facilityId)
    if (!isGlobal && !isThisFacility) return

    const ts = parseEffectiveTimestamp(row.effective_date)
    if (ts == null || ts > asOf) return

    const addonId = Number(row.iv_addon_id ?? row.iv_addon?.id)
    if (!addonId) return

    const addon = addonMap[addonId]
    if (addon) {
      if (addon.active === false) return
      const addonFacility = isBlankFacility(addon.facility_id) ? null : Number(addon.facility_id)
      if (addonFacility != null && !sameFacility(addonFacility, facilityId)) return
    }

    const candidate = {
      ts,
      isFacilitySpecific: !isGlobal,
      id: addonId,
      name: row.iv_addon?.name || addon?.name || `Add-on #${addonId}`,
      description: row.iv_addon?.description || addon?.description || '',
      catalogPrice: row.price,
      facilityId: priceFacility,
    }
    const current = winners[addonId]
    if (!current) {
      winners[addonId] = candidate
      return
    }
    if (candidate.ts > current.ts) {
      winners[addonId] = candidate
      return
    }
    if (candidate.ts === current.ts && candidate.isFacilitySpecific && !current.isFacilitySpecific) {
      winners[addonId] = candidate
    }
  })

  return Object.values(winners)
    .map(({ ts, isFacilitySpecific, ...item }) => item)
    .sort((a, b) => String(a.name).localeCompare(String(b.name)))
}

export function formatCatalogPrice(value) {
  if (value == null || value === '') return ''
  const n = Number(value)
  return Number.isFinite(n) ? n.toFixed(2) : String(value)
}
